export type LoginMethod = {
  email?: string;
  method?: 'sso' | 'db';
  redirectUrl?: string;
};

export type Namespace =
  | 'city'
  | 'integrated'
  | 'workspace'
  | 'inventory'
  | 'community-inventory'
  | 'dataset'
  | 'pathways'
  | 'bill-management'
  // Landing sentinels (not products): the post-login target can resolve to no
  // licensed products, or to doorway being unavailable. Routed to in-app pages
  // rather than launched as a product.
  | 'no-products'
  | 'unavailable';

export type Launch = {
  namespace: Namespace;
  id?: string;
};

export type Entity = {
  namespace: Namespace;
  id: string;
};

export type AuthenticatedSource = 'login' | 'session';

export type AcceptedLogin = {
  accessToken: string;
  orphan: boolean;
  acceptedTerms: boolean;
  analytics?: AnalyticsLevel;
  pendingInvitations: boolean;
  name: string;
  firstName: string;
  lastName: string;
  initials: string;
  email: string;
  target?: Launch;
  source: AuthenticatedSource;
};

export type Session = AcceptedLogin;

export type AccessKey = {
  id: string;
  name?: string;
  created: string;
  scopes: string[];
  lastUsed?: string | null;
  expiresAt?: string | null;
};

export type AccessKeySecret = {
  id: string;
  name?: string;
  created: string;
  scopes: string[];
  lastUsed?: string | null;
  expiresAt?: string | null;
  private: string;
};

export type Organisation = {
  id: number;
  name: string;
  avatar?: string;
  industry?: string;
  ssoEnabled?: boolean;
  mandatesSso?: boolean;
  ssoDomains?: string[];
  dataStorage: 'au' | 'us';
};

export type OrganisationVanityLogin = {
  id: number;
  name: string;
  avatar?: string;
};

export type UpdateOrganisation = {
  id: number;
  name: string;
  avatar?: string;
  industry?: string;
};

export type NewOrganisation = {
  name: string;
  dataStorage: 'au' | 'us';
  avatar?: string;
  industry?: string;
};

export type AnalyticsLevel = 'full' | 'limited' | 'none';

export type User = {
  id: number;
  email: string;
  analytics: AnalyticsLevel;
  firstName: string;
  lastName: string;
  jobTitle?: string;
};

export type Me = {
  id: number;
  email: string;
  analytics: AnalyticsLevel;
  firstName: string;
  lastName: string;
  jobTitle?: string;
  keys: AccessKey[];
  memberships: Membership[];
  // Pending diplomat invitations addressed to me, both kinds; the token is
  // my own copy, which accept and decline key on.
  invitations: MeInvitation[];
};

export type MeInvitation = {
  // The namespace of the thing the row invites into: an organisation, or a
  // disclosure share. Rendering, accept and decline all route on this.
  namespace: 'organisation' | 'disclosure';
  // The key of the thing the invitation is into — for a disclosure share,
  // where accepting lands.
  objectKey: string;
  token: string;
  label: string;
  issuerName: string;
  expiresAt: string;
  // The owning organisation's name and logo, resolved server-side from the
  // record's owner. Null when the record carries no owner (rows issued
  // before diplomat recorded one) or the resolution failed — the row then
  // renders without a logo.
  ownerName?: string | null;
  ownerAvatarUrl?: string | null;
};

export type MeUpdate = {
  id: number;
  analytics: AnalyticsLevel;
  firstName: string;
  lastName: string;
  jobTitle?: string;
};

export type InvitationData = OrganisationInvitationData | ShareInvitationData;

export type OrganisationInvitationData = {
  namespace: 'organisation';
  token: string;
  organisationName: string;
  avatar?: string;
  invitedBy: string;
  expiresAt: string;
};

// A diplomat disclosure share, pending in the same box; accepting opens the
// disclosure itself, keyed by objectKey.
export type ShareInvitationData = {
  namespace: 'disclosure';
  token: string;
  objectKey: string;
  label: string;
  sharedBy: string;
  expiresAt: string;
  // The owning organisation, when resolved; without it the row renders
  // without a logo.
  ownerName?: string;
  avatar?: string;
};

export type ServiceAccount = {
  id: number;
  name: string;
  description?: string | null;
  allowedScopes: string[];
  keys: AccessKey[];
};

export type NewServiceAccount = {
  name: string;
  description?: string;
};

// A row on the members list. Two sources: a friend user with a membership
// (active, or a legacy friend-pending invitation), or a diplomat invitation
// that has no user behind it yet — only an email.
export type Member = MembershipMember | InvitationMember;

export type MembershipMember = {
  source: 'membership';
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  initials: string;
  jobTitle?: string;
  lastLogin?: string;
  membership: Membership;
};

export type InvitationMember = {
  source: 'invitation';
  invitation: MemberInvitation;
};

export type MemberInvitation = {
  id: number;
  email: string;
  issuedAt: string;
  expired: boolean;
  permissions: {
    editor: boolean;
  };
};

export type Membership = {
  id: number;
  organisationId: number;
  organisationName: string;
  organisationAvatar?: string;
  inviterName?: string;
  status: 'active' | 'pending';
  invitationExpired?: boolean;
  permissions: {
    editor: boolean;
  };
};

export const Products = [
  'nzo',
  'workspaces',
  'pathways',
  'city',
  'integrated',
  'bill-management',
  'community-decarbonisation',
] as const;
export type Product = (typeof Products)[number];

export type DefaultProductOption =
  | { type: 'last-visited' }
  | { type: 'default-for-organisation' }
  | { type: 'product'; product: Product };

export type ProductLaunchPreference =
  | { type: 'last-visited' }
  | { type: 'default-for-organisation' }
  | { type: 'entity'; id: string };

export type LaunchSelection = {
  namespace: Namespace;
  preference: ProductLaunchPreference;
};

export type ProductLaunchOption = {
  name?: string;
  preference: ProductLaunchPreference;
};

export type LaunchOptions = {
  namespace: Namespace;
  options: ProductLaunchOption[];
};

export type DefaultGridProductOption = {
  label: string;
  value: ProductLaunchPreference;
};

export type DefaultGridProduct = {
  product: Product;
  selection?: ProductLaunchPreference;
  options: DefaultGridProductOption[];
};

export type Licence = {
  key: Product;
  licensed: boolean;
};

export type Flag = {
  id: number;
  name: string;
  description: string;
  enabled: boolean;
  editable: boolean;
};

export type TermsItem = {
  accepted?: boolean;
  id: number;
  name: string;
  ownerAvatarUrl: string;
  ownerName: string;
  text: string;
  declined: boolean;
  createdAt: string;
};

export type ValidFileExtension = {
  label?: string;
  fileExtension: string;
};
